/**
 * Post-drop self-check for the Pragmatic Cards view.
 *
 * Verifies that the data the frontend receives after a *successful* plan call
 * actually reflects the move: the dropped card must be gone from its source
 * route and present on its target route. When that does not hold, the board is
 * about to render the card back on its old route while the backend kept the
 * move — the "card jumps back until a refresh" symptom this module was built
 * to diagnose. The user then gets a warning snackbar telling them to refresh,
 * instead of a board that silently lies, and the mismatch is logged through
 * {@link apDebug}.
 *
 * Lifecycle: `recordDropForVerification` at the drop (Cards view drop handler)
 * → `armDropVerification` when the mutation reports success (only when the
 * call carries the recorded drop's events — the util is shared with views that
 * never record) → `expectDropRefetch` when the refetch branch runs →
 * `verifyDropAfterRefresh` when the refreshed entities land (inline response,
 * or the merge of exactly that refetch). Error/cancel paths call
 * `clearDropVerification`.
 *
 * One pending slot, last drop wins: with two drops in flight the earlier one
 * hands its slot to the later. That can skip (never mis-report) a check —
 * acceptable for a diagnostic, and the same trade the drag-preview state makes.
 */

import i18n from 'config/i18n';

import showSnackbar from 'components/shared/Snackbar';
import { apDebug } from './apDebug';

import type { PlanningEntity, PlanningEntityMap } from './types';

export interface PendingDropVerification {
  /** Ids of the dropped event(s). */
  eventIds: string[];
  /** Board key of the route the card was dragged out of. */
  sourcePlanningEntityId: string;
  /** Board key of the route the card was dropped on. */
  targetPlanningEntityId: string;
  /** True once the mutation reported success, so refreshed data must show the move. */
  armed: boolean;
  /**
   * Timestamp the drop's own refetch runs under (`setPlanResponse…({ ts })`),
   * set by the refetch branch of the plan call. The refetch-phase check only
   * accepts data carrying this timestamp: any earlier GET still in flight — a
   * websocket refetch, a window-focus refetch — would otherwise land first,
   * fail the check against pre-drop data, and both warn wrongly and consume
   * the verification before the real refetch arrives. Null until that branch
   * runs, which the refetch-phase check treats as "not mine".
   */
  expectedRefetchTs: number | null;
}

export interface DropVerificationVerdict {
  ok: boolean;
  /** What the refreshed data failed to show, empty when `ok`. */
  problems: string[];
}

/**
 * A successful drop whose refreshed data never arrives is as wrong as one whose
 * data is stale — after this long the verification reports itself as timed out
 * (log only, no snackbar: a slow network is not a bug).
 */
const VERIFICATION_TIMEOUT_MS = 10000;

let pending: PendingDropVerification | null = null;
let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

const cancelTimeout = (): void => {
  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle);
    timeoutHandle = null;
  }
};

const entityContainsEvent = (
  entity: PlanningEntity | undefined,
  eventId: string
): boolean => {
  if (!entity) return false;
  const inSchedules = (entity.schedules ?? []).some((schedule) =>
    (schedule.events ?? []).some(({ id }) => id === eventId)
  );
  const inEvents = (entity.events ?? []).some(({ id }) => id === eventId);
  return inSchedules || inEvents;
};

/**
 * Pure check of one pending drop against the refreshed entities. Only the keys
 * in `refreshedEntities` change on the board (the merge keeps the rest as-is),
 * so a source or target route missing from it is itself a problem: that
 * column keeps rendering its pre-drop state.
 *
 * A move *within* one route — a card dragged to another slot or free-time gap
 * of the route it already sits on — keeps the event exactly where it is, so
 * only the presence check applies there. Nothing upstream rejects such a drop
 * (the drop handler's only container guard is `activeIsToPlan === overIsToPlan`
 * and no dropzone excludes the card's own route), so without this split every
 * successful same-route time move would report itself as a stale board and warn
 * the user. It does leave the new start time unverified: the route recomputes
 * the start, so checking it against the requested one would warn wrongly. What
 * presence still catches for a same-route move is the route missing from the
 * refreshed data altogether — the stale-column symptom this module hunts.
 */
export const evaluateDropVerification = (
  drop: PendingDropVerification,
  refreshedEntities: PlanningEntityMap
): DropVerificationVerdict => {
  const problems: string[] = [];

  const source = refreshedEntities[drop.sourcePlanningEntityId];
  const target = refreshedEntities[drop.targetPlanningEntityId];
  const isSameRoute =
    drop.sourcePlanningEntityId === drop.targetPlanningEntityId;

  if (!isSameRoute) {
    if (!source) {
      problems.push('source route missing from refreshed data');
    } else {
      const stillOnSource = drop.eventIds.filter((eventId) =>
        entityContainsEvent(source, eventId)
      );
      if (stillOnSource.length > 0) {
        problems.push(
          `event(s) still on source route: ${stillOnSource.join(', ')}`
        );
      }
    }
  }

  if (!target) {
    problems.push('target route missing from refreshed data');
  } else {
    const missingOnTarget = drop.eventIds.filter(
      (eventId) => !entityContainsEvent(target, eventId)
    );
    if (missingOnTarget.length > 0) {
      problems.push(
        `event(s) missing on target route: ${missingOnTarget.join(', ')}`
      );
    }
  }

  return { ok: problems.length === 0, problems };
};

/** Registers the drop the next successful plan call should be checked against. */
export const recordDropForVerification = (drop: {
  eventIds: string[];
  sourcePlanningEntityId: string;
  targetPlanningEntityId: string;
}): void => {
  cancelTimeout();
  pending = { ...drop, armed: false, expectedRefetchTs: null };
};

/**
 * Marks the recorded drop as confirmed by the backend, so the next refreshed
 * entities must reflect it. No-op when nothing is recorded (calls that are not
 * a Cards-view drop, e.g. the Roster view sharing the same util), and when the
 * call carries none of the recorded drop's events: every view shares
 * `handlePlanAppointments`, so without that check a plan call from another view
 * would arm a drop that never reached a plan call of its own — a recorded drop
 * survives a confirmation popover that unmounts without `onClose`, and the
 * foreign call's refreshed data would then warn over a board that is correct.
 */
export const armDropVerification = (eventIds?: string[]): void => {
  const drop = pending;
  if (!drop) return;
  if (eventIds?.length && !eventIds.some((id) => drop.eventIds.includes(id))) {
    apDebug('dropVerification.armSkipped', { pending: drop, eventIds });
    return;
  }
  drop.armed = true;

  cancelTimeout();
  timeoutHandle = setTimeout(() => {
    timeoutHandle = null;
    if (!pending?.armed) return;
    apDebug('dropVerification.timeout', {
      pending,
      note: 'no refreshed entities landed after a successful plan call'
    });
    pending = null;
  }, VERIFICATION_TIMEOUT_MS);
};

/**
 * Records the timestamp the drop's own refetch will land under, so the
 * refetch-phase check accepts exactly that response and lets every other
 * fetch pass by untouched. Called by the plan call's refetch branch with the
 * same `ts` it hands to `setPlanResponsePlanningEntityIdentifiers`.
 */
export const expectDropRefetch = (ts: number): void => {
  if (!pending?.armed) return;
  pending.expectedRefetchTs = ts;
};

/** Drops the recorded verification, e.g. on error/cancel — nothing to check. */
export const clearDropVerification = (reason: string): void => {
  if (pending) apDebug('dropVerification.cleared', { reason, pending });
  cancelTimeout();
  pending = null;
};

/**
 * Checks the refreshed entities against the armed drop, once: whichever data
 * lands first after the success (inline response entities or the refetch
 * merge) consumes the verification. On a mismatch the user gets a warning
 * snackbar — the board is about to show the pre-drop state even though the
 * backend kept the move, and a refresh is the only way out.
 */
export const verifyDropAfterRefresh = (
  refreshedEntities: PlanningEntityMap | null | undefined,
  phase: 'inline' | 'refetch',
  refetchTs?: number
): void => {
  if (!pending?.armed || !refreshedEntities) return;

  // Only the drop's own refetch may consume the check. Every fetch runs
  // through the merge effect this is called from, so data from a fetch that
  // started before the drop (websocket refetch, window-focus refetch) would
  // otherwise land first, warn against pre-drop data, and leave the real
  // refetch unchecked. The inline phase needs no timestamp: it runs
  // synchronously inside the plan call's own success callback.
  if (phase === 'refetch' && refetchTs !== pending.expectedRefetchTs) {
    apDebug('dropVerification.refetchIgnored', {
      refetchTs,
      expectedRefetchTs: pending.expectedRefetchTs,
      pending
    });
    return;
  }

  const verdict = evaluateDropVerification(pending, refreshedEntities);
  apDebug('dropVerification.result', {
    phase,
    pending,
    // A same-route move is only checked for presence, so an `ok` verdict says
    // less there than it does for a route→route move — flagged so the log is
    // not read as a full pass.
    sameRoute:
      pending.sourcePlanningEntityId === pending.targetPlanningEntityId,
    refreshedKeys: Object.keys(refreshedEntities),
    ...verdict
  });

  cancelTimeout();
  pending = null;

  if (!verdict.ok) {
    showSnackbar({
      severity: 'warning',
      message: i18n.t('planningNotRefreshedAfterMove')
    });
  }
};

/** The current pending verification. For tests. */
export const getPendingDropVerification = (): PendingDropVerification | null =>
  pending;
